const freezePlan = plan => Object.freeze({
  ...plan,
  limits: plan.limits ? Object.freeze({ ...plan.limits }) : undefined,
  features: Object.freeze([...(plan.features || [])]),
});
const collaborationReady = process.env.REACT_APP_COLLABORATION_ENABLED === 'true';

/**
 * Catálogo comercial único do frontend.
 * Os valores pagos também são validados no backend e no banco.
 */
export const PAYMENT_CATALOG = Object.freeze({
  sensor_free: freezePlan({
    id: 'sensor_free', name: 'Gratuito Sensor',
    description: 'Acesso vitalício exclusivo à estação meteorológica adquirida.',
    monthly: 0, annual: 0, rank: 0, saleable: false, adminOnly: true, color: '#0f9f8f',
    limits: { properties: 0, fields: 0, users: 1, sensors: 1, alerts: 0, ndvi: 0 },
    features: [
      'Acesso vitalício ao módulo Sensores',
      '1 estação meteorológica vinculada',
      'Chuva, temperatura, umidade e demais leituras disponíveis',
      'Ativação exclusiva pelo administrador do ClimoraX',
    ],
  }),
  essential: freezePlan({
    id: 'essential', name: 'Essencial',
    description: 'Operação individual com os recursos agronômicos essenciais.',
    monthly: 69.90, annual: 699.00, rank: 1, saleable: true, color: '#0787b8',
    limits: { properties: 2, fields: 20, users: 1, sensors: 3, alerts: 15, ndvi: 15 },
    features: [
      '2 propriedades e 20 talhões físicos',
      'Plantios e histórico de cultivos ilimitados',
      '1 usuário, 3 sensores e 15 alertas ativos',
      ...(collaborationReady ? ['1 convidado por propriedade, sem exigir assinatura do convidado'] : []),
      'Previsão, ZARC, caderno, ordens, despesas e produtividade',
      'Irrigação em até 10 talhões',
      'RGB, CIR, terreno, curvas de nível e 15 NDVI/mês',
      'Relatórios PDF',
    ],
  }),
  professional: freezePlan({
    id: 'professional', name: 'Profissional',
    description: 'Escala, análise e colaboração para operações tecnificadas.',
    monthly: 149.00, annual: 1490.00, rank: 2, saleable: true,
    recommended: true, color: '#176dcc',
    limits: { properties: 10, fields: 100, users: 3, sensors: 10, alerts: null, ndvi: 60 },
    features: [
      'Todos os módulos e recursos do Essencial',
      '10 propriedades e 100 talhões físicos',
      'Plantios e histórico de cultivos ilimitados',
      collaborationReady ? '1 proprietário e até 2 colaboradores únicos na conta' : 'Até 2 acessos compartilhados na conta',
      '10 sensores e alertas em volume operacional alto',
      'Irrigação para todos os talhões',
      '60 NDVI/mês, comparação temporal e zonas de manejo',
      'Relatórios PDF/Excel e exportação de dados',
    ],
  }),
  management: freezePlan({
    id: 'management', name: 'Gestão',
    description: 'Governança de equipe, propriedades e operação agrícola.',
    monthly: 299.00, annual: 2990.00, rank: 3, saleable: true, color: '#07977c',
    limits: { properties: 25, fields: 300, users: 10, sensors: 25, alerts: null, ndvi: 200 },
    features: [
      '25 propriedades e 300 talhões físicos',
      'Plantios e histórico de cultivos ilimitados',
      '10 pessoas na equipe, incluindo o proprietário, e 25 sensores',
      '200 processamentos NDVI por mês',
      'Todos os recursos do Profissional',
      'Papéis e permissões por propriedade e módulo',
      'Auditoria, custos e relatórios gerenciais',
    ],
  }),
  enterprise: freezePlan({
    id: 'enterprise', name: 'Enterprise',
    description: 'Operações multicliente, integrações e limites sob contrato.',
    monthly: null, annual: null, rank: 4, saleable: false, hidden: true, paused: true,
    contactSales: true, color: '#18386c',
    features: [
      'Limites definidos em contrato',
      'Organizações, unidades e múltiplos clientes',
      'SSO, API, webhooks e integrações',
      'Dashboards e relatórios personalizados',
      'SLA e implantação assistida',
    ],
  }),

  // Preservados apenas para assinaturas e clientes existentes.
  premium: freezePlan({
    id: 'premium', name: 'Premium', monthly: 19.90, annual: 199.00,
    rank: 0.5, saleable: false, legacy: true, color: '#d59b00',
  }),
  pro: freezePlan({
    id: 'pro', name: 'Pro', monthly: 49.90, annual: 499.00,
    rank: 2.5, saleable: false, legacy: true, color: '#8a3bb1',
  }),
  addon_irrigacao: freezePlan({
    id: 'addon_irrigacao', name: 'Add-on Irrigação', monthly: 29.90,
    annual: 299.00, saleable: false, legacy: true,
  }),
  addon_satelite: freezePlan({
    id: 'addon_satelite', name: 'Add-on Satélite', monthly: 39.90,
    annual: 399.00, saleable: false, legacy: true,
  }),
});

export const COMMERCIAL_PLAN_IDS = Object.freeze([
  'essential', 'professional', 'management',
]);

export const SALEABLE_PLAN_IDS = Object.freeze([
  'essential', 'professional', 'management',
]);

export const TRIAL_PLAN = Object.freeze({
  id: 'starter', name: 'Starter Trial', days: 30,
  limits: Object.freeze({ properties: 2, fields: 10, users: 1, sensors: 2, alerts: 10, ndvi: 10 }),
});

export function formatCurrency(value) {
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2,
  });
}

export function getProductPrice(productId, period = 'mensal') {
  const product = PAYMENT_CATALOG[productId];
  if (!product) return null;
  return period === 'anual' ? product.annual : product.monthly;
}

export function getAnnualSavings(productId) {
  const product = PAYMENT_CATALOG[productId];
  if (!product?.monthly || !product?.annual) return 0;
  return product.monthly * 12 - product.annual;
}

export function getPlanName(planId) {
  if (planId === 'starter') return TRIAL_PLAN.name;
  return PAYMENT_CATALOG[planId]?.name || 'Plano não identificado';
}

export function isSaleablePlan(planId) {
  return SALEABLE_PLAN_IDS.includes(planId);
}

export function isPlanUpgrade(currentPlan, targetPlan) {
  const currentRank = currentPlan === 'starter' ? 0 : PAYMENT_CATALOG[currentPlan]?.rank;
  const targetRank = PAYMENT_CATALOG[targetPlan]?.rank;
  return Number.isFinite(currentRank) && Number.isFinite(targetRank) && targetRank > currentRank;
}

export function getUpgradeOptions(currentPlan) {
  return COMMERCIAL_PLAN_IDS
    .map(id => PAYMENT_CATALOG[id])
    .filter(plan => plan.contactSales || (plan.saleable && isPlanUpgrade(currentPlan, plan.id)));
}
